import type { AnyInbound, AuthResp, BroadcastEvent, BroadcastEventMap, BroadcastMsg } from '../types/hub';
import { ConnectionState, useHubStore } from '../context/HubStore';
import { useHubResumeStore } from '../context/HubResumeStore';
import config from '../config';
import { addBreadcrumb, captureMessage, setContext, setTag } from '@sentry/react';

const STEP_TIMEOUT_MS = 5000;
const MAX_BACKOFF_MS = 12_000;

/** Exponential backoff with jitter */
const nextDelay = (attempt: number): number => {
  const base = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
  const jitter = base * (Math.random() * 0.4 - 0.2);
  return base + jitter;
};

type AnyBroadcastHandler = (payload: BroadcastEventMap[BroadcastEvent]) => void;

interface AuthParams {
  jwt: string;
  locationId: number;
  deviceId: string;
  clientVersion: string;
  clientApp: string;
}

export class HubClient {
  private ws?: WebSocket;
  private connectionId = 0; // Monotonic ID to detect stale callbacks
  private reconnectAttempts = 0;
  private reconnectTimer?: number;
  private stepTimer?: number;

  private readonly decoder = new TextDecoder();
  private readonly broadcastHandlers = new Map<BroadcastEvent, Set<AnyBroadcastHandler>>();

  private deviceId?: string;
  private readonly recentLocalMessageIds = new Set<string>();

  constructor(private readonly url: string, private readonly getAuth: () => Promise<AuthParams>) {
    setTag('hub.url', url);
    setTag('hub.env', config.env);
    setTag('appVersion', config.release);
  }

  // ─────────────────────────────────────────────────────────────────
  // State management
  // ─────────────────────────────────────────────────────────────────

  private get state(): ConnectionState {
    return useHubStore.getState().connectionState;
  }

  private setState(newState: ConnectionState): void {
    const prev = this.state;
    if (prev === newState) {
      return;
    }

    addBreadcrumb({
      category: 'ws',
      level: 'info',
      message: `state:${prev}->${newState}`,
    });
    useHubStore.getState().setConnectionState(newState);
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== undefined) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    if (this.stepTimer !== undefined) {
      clearTimeout(this.stepTimer);
      this.stepTimer = undefined;
    }
  }

  private scheduleReconnect(): void {
    this.clearTimers();
    const delay = nextDelay(this.reconnectAttempts);
    addBreadcrumb({
      category: 'ws',
      data: { attempt: this.reconnectAttempts, delayMs: Math.round(delay) },
      level: 'info',
      message: 'reconnect:scheduled',
    });
    this.reconnectAttempts++;
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private failConnection(reason: string): void {
    addBreadcrumb({ category: 'ws', level: 'warning', message: `fail:${reason}` });
    this.clearTimers();
    this.connectionId++; // Invalidate any pending async operations

    const { ws } = this;
    this.ws = undefined;
    if (ws) {
      try {
        ws.close();
      } catch {
        // Ignore close errors
      }
    }

    this.setState(ConnectionState.Disconnected);
    this.scheduleReconnect();
  }

  // ─────────────────────────────────────────────────────────────────
  // Connection lifecycle
  // ─────────────────────────────────────────────────────────────────

  connect(): void {
    // Prevent duplicate connections
    if (this.state !== ConnectionState.Disconnected) {
      addBreadcrumb({
        category: 'ws',
        data: { currentState: this.state },
        level: 'info',
        message: 'connect:skipped',
      });
      return;
    }

    this.clearTimers();
    this.connectionId++;
    const connId = this.connectionId;

    addBreadcrumb({ category: 'ws', data: { connId, url: this.url }, level: 'info', message: 'connect:start' });
    this.setState(ConnectionState.Connecting);

    // Pre-fetch auth before opening socket to avoid server-side auth timeout
    this.connectWithAuth(connId);
  }

  private async connectWithAuth(connId: number): Promise<void> {
    // Timeout for entire pre-auth + socket open phase
    this.stepTimer = window.setTimeout(() => {
      if (this.connectionId !== connId) {
        return;
      }
      this.failConnection('connect_timeout');
    }, STEP_TIMEOUT_MS);

    // Fetch auth params first (may involve token refresh)
    let auth: AuthParams;
    try {
      auth = await this.getAuth();
    } catch (error) {
      if (this.connectionId !== connId) return;
      captureMessage('auth:getAuth_failed', { level: 'error', extra: { err: String(error) } });
      this.failConnection('auth_fetch_failed');
      return;
    }

    if (this.connectionId !== connId) {
      return;
    }

    // Auth is ready, now open socket
    this.openSocket(connId, auth);
  }

  private openSocket(connId: number, auth: AuthParams): void {
    if (this.connectionId !== connId || this.state !== ConnectionState.Connecting) {
      return;
    }

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (error) {
      if (this.connectionId !== connId) return;
      captureMessage('ws:create_failed', { level: 'error', extra: { err: String(error) } });
      this.failConnection('socket_create_failed');
      return;
    }

    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    // oxlint-disable-next-line prefer-add-event-listener -- WebSocket on* intentionally replaces handler per connection
    ws.onopen = () => {
      if (this.connectionId !== connId || this.ws !== ws) {
        return;
      }
      this.handleOpen(connId, auth);
    };

    // oxlint-disable-next-line prefer-add-event-listener
    ws.onerror = () => {
      if (this.connectionId !== connId || this.ws !== ws) {
        return;
      }
      // Error events are followed by close; just log
      addBreadcrumb({ category: 'ws', level: 'info', message: 'error' });
    };

    // oxlint-disable-next-line prefer-add-event-listener
    ws.onclose = (ev) => {
      if (this.connectionId !== connId || this.ws !== ws) {
        return;
      }
      addBreadcrumb({
        category: 'ws',
        data: { code: ev.code, reason: ev.reason, wasClean: ev.wasClean },
        level: 'warning',
        message: 'close',
      });
      this.failConnection('socket_closed');
    };

    // oxlint-disable-next-line prefer-add-event-listener
    ws.onmessage = (ev) => {
      if (this.connectionId !== connId || this.ws !== ws) {
        return;
      }
      this.handleMessage(ev, connId);
    };
  }

  private handleOpen(connId: number, auth: AuthParams): void {
    if (this.connectionId !== connId || this.state !== ConnectionState.Connecting) {
      return;
    }

    this.clearTimers();

    addBreadcrumb({ category: 'ws', level: 'info', message: 'open' });
    this.setState(ConnectionState.Authenticating);

    // Timeout for auth response from server
    this.stepTimer = window.setTimeout(() => {
      if (this.connectionId !== connId) {
        return;
      }
      this.failConnection('auth_timeout');
    }, STEP_TIMEOUT_MS);

    // Send auth immediately - credentials were pre-fetched
    this.deviceId = auth.deviceId;
    setTag('hub.locationId', String(auth.locationId));
    setContext('hub', { locationId: auth.locationId });

    const resume = useHubResumeStore.getState().credentials;
    addBreadcrumb({
      category: 'auth',
      data: { hasResume: !!resume, locationId: auth.locationId },
      level: 'info',
      message: 'auth:send',
    });

    try {
      this.ws!.send(
        JSON.stringify({
          jwt: auth.jwt,
          locationId: auth.locationId,
          metadata: { clientApp: auth.clientApp, clientId: auth.deviceId, clientVersion: auth.clientVersion },
          type: 'auth.v1',
          ...(resume && { resume }),
        })
      );
    } catch (error) {
      if (this.connectionId !== connId) return;
      captureMessage('auth:send_failed', { level: 'error', extra: { err: String(error) } });
      this.failConnection('auth_send_failed');
    }
  }

  private handleAuthOk(connId: number, resp: AuthResp): void {
    if (this.connectionId !== connId || this.state !== ConnectionState.Authenticating) {
      return;
    }

    this.clearTimers();

    // Store resume credentials for future reconnects
    useHubResumeStore.getState().setCredentials({
      connectionId: resp.connectionId,
      resumeToken: resp.resumeToken,
    });

    addBreadcrumb({
      category: 'auth',
      data: { reconnectCount: resp.reconnectCount },
      level: 'info',
      message: 'auth:ok',
    });
    this.setState(ConnectionState.Subscribing);

    // Timeout for subscribe ack
    this.stepTimer = window.setTimeout(() => {
      if (this.connectionId !== connId) {
        return;
      }
      this.failConnection('subscribe_timeout');
    }, STEP_TIMEOUT_MS);

    try {
      this.ws!.send(JSON.stringify({ data: {}, type: 'pos.broadcast.subscribe.v1' }));
      addBreadcrumb({ category: 'broadcast', level: 'info', message: 'subscribe:send' });
    } catch (error) {
      if (this.connectionId !== connId) return;
      captureMessage('subscribe:send_failed', { level: 'error', extra: { err: String(error) } });
      this.failConnection('subscribe_send_failed');
    }
  }

  private handleSubscribeOk(connId: number): void {
    if (this.connectionId !== connId || this.state !== ConnectionState.Subscribing) {
      return;
    }

    this.clearTimers();
    this.reconnectAttempts = 0; // Only reset on full connection success
    addBreadcrumb({ category: 'broadcast', level: 'info', message: 'subscribe:ok' });
    this.setState(ConnectionState.Connected);
  }

  private handleError(connId: number, data: { code?: string; message?: string }): void {
    if (this.connectionId !== connId || this.state === ConnectionState.Disconnected) {
      return;
    }

    const errorCode = data.code ?? 'UNKNOWN';
    const errorMsg = data.message ?? 'No message';

    captureMessage('hub:error_response', {
      extra: { code: errorCode, message: errorMsg, state: this.state },
      level: 'warning',
    });

    // Clear potentially stale resume credentials on auth errors
    if (this.state === ConnectionState.Authenticating) {
      useHubResumeStore.getState().clearCredentials();
      addBreadcrumb({ category: 'auth', level: 'warning', message: 'auth:failed_cleared_resume' });
    }

    this.failConnection(`error_${errorCode}`);
  }

  close(): void {
    addBreadcrumb({ category: 'ws', level: 'info', message: 'close:manual' });
    this.clearTimers();
    this.connectionId++; // Invalidate any pending callbacks

    const { ws } = this;
    this.ws = undefined;
    if (ws) {
      try {
        ws.close();
      } catch {
        // Ignore
      }
    }

    this.setState(ConnectionState.Disconnected);
  }

  // ─────────────────────────────────────────────────────────────────
  // Message handling
  // ─────────────────────────────────────────────────────────────────

  private handleMessage(ev: MessageEvent, connId: number): void {
    let raw: string;
    try {
      raw = typeof ev.data === 'string' ? ev.data : this.decoder.decode(ev.data as ArrayBuffer);
    } catch {
      captureMessage('ws:decode_failed', { level: 'error' });
      return;
    }

    let message: AnyInbound;
    try {
      message = JSON.parse(raw);
    } catch {
      // oxlint-disable-next-line no-control-regex -- intentionally detecting control chars
      const hasCtl = /[^\x09\x0A\x0D\x20-\x7E]/.test(raw);
      captureMessage('ws:json_parse_failed', {
        extra: { rawPreview: hasCtl ? '<non-text>' : raw.slice(0, 200) },
        level: 'error',
      });
      return;
    }

    // Ping/pong keep-alive
    if (message.type === 'ping' && message.version === 'v1') {
      this.sendRaw({ data: { timestamp: message.data?.timestamp }, type: 'pong', version: 'v1' });
      return;
    }

    if (message.type === 'keepalive.v1') {
      this.sendRaw(message);
      return;
    }

    // Auth response
    if (message.type === 'auth.v1.ok') {
      this.handleAuthOk(connId, message);
      return;
    }

    // Error response - handle auth/subscribe failures
    if (message.type === 'error') {
      this.handleError(connId, message.data ?? {});
      return;
    }

    // Subscribe ack
    if (message.type === 'pos.broadcast.subscribe.v1.ok') {
      this.handleSubscribeOk(connId);
      return;
    }

    // Broadcast message
    if (message.type === 'pos.broadcast.v1') {
      this.handleBroadcast(message as BroadcastMsg);
      return;
    }

    // Unhandled message
    if (['local', 'localQa'].includes(config.env)) {
      // oxlint-disable-next-line no-console -- intentional debug log for local dev
      console.log('[hub] unhandled message', message.type);
    }
    addBreadcrumb({ category: 'ws', level: 'info', message: `unhandled:${message.type}` });
  }

  private handleBroadcast(message: BroadcastMsg): void {
    const { event, payload, sourceDeviceId, ...rest } = message.data ?? {};

    if (typeof event !== 'string') {
      captureMessage('broadcast:invalid_event', { level: 'warning' });
      return;
    }

    const eventPayload = payload ?? (Object.keys(rest).length > 0 ? rest : undefined);
    if (eventPayload === undefined) {
      captureMessage('broadcast:missing_payload', { extra: { event }, level: 'warning' });
      return;
    }

    // Deduplicate by messageId (covers both self-echo and server relay)
    if (message.messageId && this.recentLocalMessageIds.has(message.messageId)) {
      addBreadcrumb({ category: 'broadcast', level: 'info', message: `skip_echo:${event}` });
      return;
    }

    // Fallback: skip self-broadcasts by sourceDeviceId (for messages without messageId)
    if (sourceDeviceId && sourceDeviceId === this.deviceId) {
      addBreadcrumb({ category: 'broadcast', level: 'info', message: `skip_self:${event}` });
      return;
    }

    addBreadcrumb({ category: 'broadcast', level: 'info', message: `recv:${event}` });

    const handlers = this.broadcastHandlers.get(event as BroadcastEvent);
    if (!handlers || handlers.size === 0) {
      addBreadcrumb({ category: 'broadcast', level: 'info', message: `unhandled:${event}` });
      return;
    }

    // Copy to array to safely handle concurrent unsubscription during iteration
    for (const fn of Array.from(handlers)) {
      try {
        fn(eventPayload as BroadcastEventMap[BroadcastEvent]);
      } catch (error) {
        captureMessage('broadcast:handler_error', { level: 'warning', extra: { event, err: String(error) } });
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Send helpers
  // ─────────────────────────────────────────────────────────────────

  private sendRaw(payload: unknown): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    try {
      this.ws.send(JSON.stringify(payload));
      return true;
    } catch {
      return false;
    }
  }

  private isReady(): boolean {
    return this.state === ConnectionState.Connected && this.ws?.readyState === WebSocket.OPEN;
  }

  // ─────────────────────────────────────────────────────────────────
  // Broadcast API
  // ─────────────────────────────────────────────────────────────────

  onBroadcast<K extends BroadcastEvent>(event: K, handler: (payload: BroadcastEventMap[K]) => void): () => void {
    let set = this.broadcastHandlers.get(event);
    if (!set) {
      set = new Set();
      this.broadcastHandlers.set(event, set);
    }
    set.add(handler as AnyBroadcastHandler);

    return () => {
      const current = this.broadcastHandlers.get(event);
      if (!current) {
        return;
      }
      current.delete(handler as AnyBroadcastHandler);
      if (current.size === 0) {
        this.broadcastHandlers.delete(event);
      }
    };
  }

  broadcast<K extends BroadcastEvent>(event: K, payload: BroadcastEventMap[K]): void {
    if (!this.isReady()) {
      addBreadcrumb({ category: 'broadcast', level: 'info', message: `send_skipped:${event}` });
      return;
    }

    const messageId = crypto.randomUUID();
    const message: BroadcastMsg<K> = {
      data: { event, payload, sourceDeviceId: this.deviceId },
      messageId,
      type: 'pos.broadcast.v1',
    };

    if (this.sendRaw(message)) {
      addBreadcrumb({ category: 'broadcast', level: 'info', message: `send:${event}` });
      this.recentLocalMessageIds.add(messageId);
      setTimeout(() => this.recentLocalMessageIds.delete(messageId), 5000);
    } else {
      captureMessage('broadcast:send_failed', { extra: { event }, level: 'warning' });
    }
  }
}
